import React, { useCallback, useEffect, useMemo, useState } from 'react';
import http from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, RefreshCw, Download, Trash2, Users, DollarSign, Phone, Mail } from 'lucide-react';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const fmtMoney = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);

const TIER_OPTIONS = [
  ['all', 'All tiers'],
  ['basic', 'Basic ($3)'],
  ['standard', 'Standard ($36)'],
  ['featured', 'Featured ($120)'],
];
const SUB_TYPE_OPTIONS = [
  ['all', 'All submissions'],
  ['business', 'Businesses only'],
  ['event', 'Events only'],
];

export default function AdminContactsPanel() {
  const [tab, setTab] = useState('general');
  const [general, setGeneral] = useState([]);
  const [upgraded, setUpgraded] = useState([]);
  const [subType, setSubType] = useState('all');
  const [tier, setTier] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'general') {
        const params = subType !== 'all' ? { submission_type: subType } : {};
        const r = await http.get('/admin/contacts/general', { params }).then(r => r.data);
        setGeneral(r.rows || []);
      } else {
        const params = tier !== 'all' ? { tier } : {};
        const r = await http.get('/admin/contacts/upgraded', { params }).then(r => r.data);
        setUpgraded(r.rows || []);
      }
      setLastRefresh(new Date());
      setSelected(new Set());
    } catch (err) {
      toast({ title: 'Load failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setLoading(false); }
  }, [tab, subType, tier]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected(new Set()); }, [tab]);

  const rows = tab === 'general' ? general : upgraded;

  const toggleRow = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const allSelected = rows.length > 0 && rows.every(r => selected.has(r.id));
  const someSelected = rows.some(r => selected.has(r.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Remove ${ids.length} contact${ids.length === 1 ? '' : 's'} from the ${tab} list? (The underlying submission / payment stays intact.)`)) return;
    setBusy('delete');
    try {
      await http.post('/admin/contacts/delete', { kind: tab, ids });
      toast({ title: 'Contacts removed', description: `${ids.length} row${ids.length === 1 ? '' : 's'} hidden.` });
      await load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setBusy(null); }
  };

  const exportList = async (fmt, onlySelected) => {
    setBusy(`export:${fmt}:${onlySelected}`);
    try {
      const path = tab === 'general' ? '/admin/contacts/general/export' : '/admin/contacts/upgraded/export';
      const params = { fmt };
      if (onlySelected) params.ids = [...selected].join(',');
      else if (tab === 'general' && subType !== 'all') params.submission_type = subType;
      else if (tab === 'upgraded' && tier !== 'all') params.tier = tier;
      const resp = await http.get(path, { params, responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      const cd = resp.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      a.download = m ? m[1] : `pbb-contacts.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast({ title: 'Export ready', description: a.download });
    } catch (err) {
      toast({ title: 'Export failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setBusy(null); }
  };

  const chip = useMemo(() => {
    if (tab === 'general' && subType !== 'all') return SUB_TYPE_OPTIONS.find(o => o[0] === subType)?.[1];
    if (tab === 'upgraded' && tier !== 'all') return TIER_OPTIONS.find(o => o[0] === tier)?.[1];
    return null;
  }, [tab, subType, tier]);

  return (
    <div className="space-y-4" data-testid="admin-contacts-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-black text-xl text-white flex items-center gap-2">
            <Users size={18} className="text-emerald-400"/> Contacts
          </h2>
          <p className="text-xs text-gray-500 max-w-2xl">
            Auto-built running contact lists. <b>General</b> gathers everyone who submitted
            a Golden-Map business or calendar event. <b>Upgraded Members</b> gathers everyone
            who paid for a subscription via Stripe or PayPal. Phone numbers are
            auto-formatted. Removals are soft — the original record stays for audit.
          </p>
        </div>
        <div className="inline-flex rounded-full border border-neutral-800 overflow-hidden text-xs">
          <button onClick={() => setTab('general')} data-testid="contacts-tab-general"
            className={`px-3 py-1.5 inline-flex items-center gap-1 ${tab === 'general' ? 'bg-emerald-500 text-black font-semibold' : 'text-gray-400 hover:bg-neutral-800'}`}>
            <Users size={12}/> General ({general.length || 0})
          </button>
          <button onClick={() => setTab('upgraded')} data-testid="contacts-tab-upgraded"
            className={`px-3 py-1.5 inline-flex items-center gap-1 ${tab === 'upgraded' ? 'bg-emerald-500 text-black font-semibold' : 'text-gray-400 hover:bg-neutral-800'}`}>
            <DollarSign size={12}/> Upgraded Members ({upgraded.length || 0})
          </button>
        </div>
      </div>

      {/* Filters + actions */}
      <div className="flex items-center gap-2 flex-wrap">
        {tab === 'general' ? (
          <select value={subType} onChange={e => setSubType(e.target.value)}
            data-testid="contacts-general-type-filter"
            className="bg-black border border-neutral-700 rounded-full text-xs text-gray-200 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/40">
            {SUB_TYPE_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        ) : (
          <select value={tier} onChange={e => setTier(e.target.value)}
            data-testid="contacts-upgraded-tier-filter"
            className="bg-black border border-neutral-700 rounded-full text-xs text-gray-200 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/40">
            {TIER_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        )}
        {chip && <span className="text-[10px] text-gray-400">Filter: {chip}</span>}
        <button onClick={load} disabled={loading} data-testid="contacts-refresh"
          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-neutral-700 hover:bg-neutral-800">
          {loading ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>} Refresh
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => exportList('xlsx', false)} disabled={!rows.length || busy?.startsWith('export')}
            data-testid="contacts-export-xlsx-all"
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-black font-semibold disabled:opacity-50">
            {busy === 'export:xlsx:false' ? <Loader2 size={12} className="animate-spin"/> : <Download size={12}/>} Excel · all
          </button>
          <button onClick={() => exportList('csv', false)} disabled={!rows.length || busy?.startsWith('export')}
            data-testid="contacts-export-csv-all"
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-neutral-700 hover:bg-neutral-800 text-white">
            <Download size={12}/> CSV · all
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-100 flex items-center gap-3 flex-wrap"
             data-testid="contacts-bulk-bar">
          <b>{selected.size}</b> selected
          <span className="text-amber-200/70 text-xs">—</span>
          <button onClick={() => exportList('xlsx', true)} disabled={busy?.startsWith('export')}
            data-testid="contacts-bulk-export-xlsx"
            className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-emerald-600 hover:bg-emerald-500 text-black font-semibold disabled:opacity-50">
            <Download size={12}/> Excel · selected
          </button>
          <button onClick={() => exportList('csv', true)} disabled={busy?.startsWith('export')}
            data-testid="contacts-bulk-export-csv"
            className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-neutral-700 hover:bg-neutral-800 text-white">
            <Download size={12}/> CSV · selected
          </button>
          <button onClick={bulkDelete} disabled={busy === 'delete'}
            data-testid="contacts-bulk-delete"
            className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-red-500/40 text-red-200 hover:bg-red-500/15 disabled:opacity-50">
            {busy === 'delete' ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>} Remove selected
          </button>
          <button onClick={() => setSelected(new Set())}
            className="ml-auto text-xs text-amber-200/70 hover:text-white underline">Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-[#1f1f1f] bg-[#0f0f0f] overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-gray-500"><Loader2 className="animate-spin inline mr-2" size={14}/> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-gray-500 text-sm">No contacts match the current filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500 bg-black/40 text-left">
                <tr>
                  <th className="px-3 py-2 w-10">
                    <input type="checkbox" checked={allSelected}
                      ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                      onChange={toggleAll}
                      data-testid="contacts-select-all"
                      className="h-4 w-4 accent-emerald-500 cursor-pointer"/>
                  </th>
                  <th className="px-3 py-2">Member</th>
                  <th className="px-3 py-2">Contact</th>
                  {tab === 'general' ? (
                    <>
                      <th className="px-3 py-2">Submission</th>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Status</th>
                    </>
                  ) : (
                    <>
                      <th className="px-3 py-2">Tier</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2">Paid at</th>
                      <th className="px-3 py-2">Method</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const checked = selected.has(r.id);
                  return (
                    <tr key={r.id} className={`border-t border-[#1a1a1a] ${checked ? 'bg-emerald-500/5' : 'hover:bg-neutral-900/40'}`}
                      data-testid={`contacts-row-${r.id}`}>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={checked} onChange={() => toggleRow(r.id)}
                          data-testid={`contacts-select-${r.id}`}
                          className="h-4 w-4 accent-emerald-500 cursor-pointer"/>
                      </td>
                      <td className="px-3 py-2 text-white font-semibold">{r.memberName}</td>
                      <td className="px-3 py-2 text-gray-300">
                        <div className="flex items-center gap-1"><Mail size={10} className="text-gray-500"/> {r.email || <span className="text-gray-600 italic">—</span>}</div>
                        <div className="flex items-center gap-1 mt-0.5"><Phone size={10} className="text-gray-500"/> {r.phone || <span className="text-gray-600 italic">—</span>}</div>
                      </td>
                      {tab === 'general' ? (
                        <>
                          <td className="px-3 py-2">
                            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border mr-1 ${r.submissionType === 'business' ? 'bg-indigo-500/15 border-indigo-500/40 text-indigo-300' : 'bg-pink-500/15 border-pink-500/40 text-pink-300'}`}>
                              {r.submissionType}
                            </span>
                            <span className="text-white">{r.submissionName}</span>
                          </td>
                          <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{fmtDate(r.submittedAt)}</td>
                          <td className="px-3 py-2 text-gray-400">{r.status || '—'}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2 text-gray-300">{r.tierLabel || r.tier || '—'}</td>
                          <td className="px-3 py-2 text-right font-bold text-emerald-300">{fmtMoney(r.amount)}</td>
                          <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{fmtDate(r.paidAt)}</td>
                          <td className="px-3 py-2">
                            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${r.method === 'stripe' ? 'bg-indigo-500/15 border-indigo-500/40 text-indigo-300' : 'bg-blue-500/15 border-blue-500/40 text-blue-300'}`}>
                              {r.method}
                            </span>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-[10px] text-gray-500 text-right">
        {lastRefresh && <>Last refreshed {lastRefresh.toLocaleTimeString()} · {rows.length} rows shown</>}
      </div>
    </div>
  );
}
